import React, { FC, useEffect, useState, useCallback, useMemo, memo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { stringify } from 'query-string';
import { useMedia } from 'react-use';
import { FilterBoxProps } from './index';
import Icon, { Icons } from '../../Icon';
import { ParametrizedPathRegExp, isBrandOV, isMarketGB, isBrandAC } from '../../../constants/main';
import { IPriceRange } from '../../../redux/filters/filter.duck.interface';
import { FilterDuck } from '../../../redux/filters/filter.duck';
import UIDuck from '../../../redux/commonDucks/ui.duck';
import { Redux } from '../../../redux';
import GlobalDuck from '../../../redux/global/global.duck';
import { filtersToUrl, modifyFilters } from '../../../services/filters/utils/Filters.utils';
import { buildParameterizedPathname } from '@utils/url.utils';
import { PRICES, FUEL, GEARBOX } from '../../../services';
import PriceSelectRange from './sections/PriceSelectRange';
import ButtonsGroup from './sections/ButtonsGroup';
import { theme } from '../../../styles/theme';
import { useTranslations } from '@hooks/useTranslations';
import { useCustomRouter } from '@hooks/useCustomRouter';
import { useCarStockJourneyCheck } from '@hooks/useCarStockJourneyCheck';
import { useGTM } from '@hooks/useGTM';
import { HandledComponentError } from '@components/ErrorHandling';
import { removeLanguageFromPathname } from '@utils/misc.utils';
import { removeLastCharacterFromPathname } from '@utils/appConfig.utils';
import { Link } from '@components/v2/link';

const FilterBoxTemplate: FC<FilterBoxProps> = memo(({ className, onChange, onReset }) => {
    try {
        const { t } = useTranslations();
        const dispatch = useDispatch();
        const { isStockJourney } = useCarStockJourneyCheck();

        const { pushGTMAction } = useGTM();

        const filters = useSelector((state: Redux) => FilterDuck.getFilters(state));
        const availableFilters = useSelector((state: Redux) => FilterDuck.getFilteredFilters(state));
        const sort = useSelector((state: Redux) => FilterDuck.getSorting(state));
        const filterCategories = useSelector((state: Redux) => FilterDuck.getFilterCategories(state));
        const priceRange = useSelector((state: Redux) => FilterDuck.getPriceRange(state));
        const isRouting = useSelector((state: Redux) => UIDuck.getIsRouting(state));
        const carJourney = useSelector((state: Redux) => GlobalDuck.getCarJourney(state));
        const selectedFiltersCount = useMemo(() => filters.filter((filter) => !!filter.value).length, [filters]);

        // component state
        const [mobileHide, setMobileHide] = useState(true);
        const [oldPrices, setOldPrices] = useState(priceRange);

        const router = useCustomRouter();
        const q = typeof window !== 'undefined' ? location.search : stringify(router.query);
        const queryString = filtersToUrl(filterCategories, filters, sort, q, null, null, null, priceRange);
        const isHigherThanMediumBreakPoint = useMedia('(min-width: 992px)');

        const formattedPathname = buildParameterizedPathname(
            router.asPath.replace(ParametrizedPathRegExp, ''),
            carJourney
        );

        const filterButtons = useMemo(
            () =>
                filters.map((filter) => ({
                    ...filter,
                    disabled:
                        availableFilters.find((filteredFilter) => filter.name === filteredFilter.name) === undefined,
                })),
            [filters, availableFilters]
        );

        const priceData = useMemo(() => filterButtons.find((field) => field.parent === PRICES), [filterButtons]);
        const fuelData = useMemo(() => filterButtons.filter((field) => field.parent === FUEL), [filterButtons]);
        const gearboxData = useMemo(() => filterButtons.filter((field) => field.parent === GEARBOX), [filterButtons]);

        // setting URL based on filter
        useEffect(() => {
            if (filters) {
                const doQueriesMatch = window.location.search === queryString;

                const path = router.asPath.replace(ParametrizedPathRegExp, '');

                const doPathNamesMatch =
                    removeLastCharacterFromPathname(path, '/') ===
                    removeLastCharacterFromPathname(formattedPathname, '/');

                if ((!doQueriesMatch || !doPathNamesMatch) && !isRouting && carJourney) {
                    router.replace(`${formattedPathname}${queryString}`, { scroll: false });
                }
            }
        }, [queryString, formattedPathname]);

        const handleChange = useCallback(
            (name: string, value: boolean | IPriceRange) => {
                const newFilters = modifyFilters(filters, name, value);
                dispatch(FilterDuck.setFilters(newFilters));

                onChange?.(name, newFilters);

                pushGTMAction(name, value, oldPrices, { fireSPAEvent: true });

                if (name === PRICES) {
                    setOldPrices(value as IPriceRange);
                }
            },
            [filters, oldPrices, onChange, dispatch, pushGTMAction]
        );

        const resetFilters = () => {
            onReset?.();

            dispatch(FilterDuck.resetFilters(filters));
            const formattedPathname = buildParameterizedPathname(
                removeLanguageFromPathname(window.location.pathname),
                carJourney
            );
            router.replace(formattedPathname, { scroll: false });
        };

        useEffect(() => {
            if (isHigherThanMediumBreakPoint && mobileHide) {
                setMobileHide(false);
            }
        }, [isHigherThanMediumBreakPoint, mobileHide]);

        return (
            <div
                id="top-level-grid"
                className={`${className} ${isStockJourney ? 'stock-journey' : ''} ${
                    mobileHide ? 'collapsed-grid' : 'expanded-grid'
                }`}
            >
                <button
                    className={`toggle-filters  ${mobileHide ? 'toggled' : ''}`}
                    onClick={() => setMobileHide(!mobileHide)}
                >
                    <span>
                        {!isBrandAC && <Icon name={Icons.Settings} width={32} height={32} color={theme.colors.white} />}
                        {t('filters.filters')}

                        {selectedFiltersCount > 0 && (
                            <span className="filters-title-badge">{selectedFiltersCount}</span>
                        )}
                    </span>
                    <Icon
                        className={mobileHide ? 'isClosed' : 'isOpen'}
                        name={
                            mobileHide
                                ? !isBrandAC
                                    ? Icons.Plus
                                    : Icons.ChevronDown
                                : !isBrandAC
                                ? Icons.Minus
                                : Icons.ChevronDown
                        }
                        width={20}
                        height={20}
                        color={theme.colors.white}
                    />
                </button>

                <div className="budget-title">{t(`filters.category.budget`)}</div>
                <PriceSelectRange data={priceData} handleChange={handleChange} priceRange={priceRange} />

                <div className="fuel-title">{t('filters.category.fuel')}</div>
                <ButtonsGroup data={fuelData} handleChange={handleChange} groupType="fuel" />

                {(!isBrandOV || !isMarketGB) && (
                    <>
                        <div className="gearbox-title">{t('filters.category.gearbox')}</div>
                        <ButtonsGroup data={gearboxData} handleChange={handleChange} groupType="gearbox" />
                    </>
                )}
                <Link
                    isUnderline={isBrandAC}
                    className="reset-filters-button"
                    txt={t('filters.reset')}
                    dataTestid="TESTING_MOBILE_RESET_BTN"
                    withParent={false}
                    id="TESTING_RESET"
                    onClick={resetFilters}
                >
                    <button
                        className="reset-filters-button"
                        onClick={resetFilters}
                        id="TESTING_RESET"
                        data-testid="TESTING_MOBILE_RESET_BTN"
                    >
                        {t(`filters.reset`)}
                    </button>
                </Link>
                <button
                    className={`show-if-mobile mobile-filter-close-button ${mobileHide ? 'hidden' : ''}`}
                    onClick={() => setMobileHide(true)}
                >
                    {t('filters.hideMore')}
                    <Icon height={8} width={14} name={Icons.ChevronDown} />
                </button>
            </div>
        );
    } catch (e) {
        return <HandledComponentError error={e} />;
    }
});
FilterBoxTemplate.displayName = 'FilterBoxTemplate';
export default FilterBoxTemplate;
